import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

/**
 * SYSTEM_DESIGN.md §6.3 / INC-04 — Botones de formulario.
 *
 * El primario va SIEMPRE PRIMERO, después "Cancelar". El orden lo fija el
 * componente, así que no se puede invertir desde el call site — que era
 * exactamente el problema (4 layouts distintos y ambos órdenes conviviendo).
 *
 * Ambos botones comparten tamaño dimensional por el mismo motivo.
 *
 * `primaryLabel` sigue el formato [Verbo] + [Entidad]: "Crear Venue",
 * "Guardar cambios". No pongas solo "Crear" ni solo "Guardar".
 */
interface FormActionsProps {
  /** Formato [Verbo] + [Entidad]. Ej: "Crear Beneficio". */
  primaryLabel: string;
  onPrimary?: () => void;
  /** Si se omite, no se renderiza "Cancelar" (formularios sin salida). */
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
  type?: "submit" | "button";
  /**
   * Motivo por el que el primario está deshabilitado. Se muestra como tooltip.
   * Un botón gris sin explicación es un callejón sin salida: si lo deshabilitás,
   * decí por qué.
   */
  disabledReason?: string;
  /** Alineación del bloque. El spec no la fija; el default centra. */
  align?: "center" | "end";
  /** Ícono del primario. El mockup usa un check; un submit distinto puede cambiarlo. */
  primaryIcon?: string;
  /** Primario de marca por defecto. `danger` es el confirm destructivo. */
  primarySeverity?: "danger";
}

export const FormActions = ({
  primaryLabel,
  onPrimary,
  onCancel,
  loading,
  disabled,
  type = "submit",
  align = "center",
  disabledReason,
  primaryIcon = "pi pi-check",
  primarySeverity,
}: FormActionsProps) => (
  <div
    className={`form-actions flex flex-wrap justify-content-${align} mt-4`}
    style={{ gap: "var(--space-18)" }}
  >
    {/* Un botón `disabled` no dispara eventos de hover, así que el tooltip va
        sobre un wrapper y no sobre el botón. */}
    <span
      className="form-actions__primary"
      data-testid="form-actions-primary"
      data-pr-tooltip={disabled ? disabledReason : undefined}
      data-pr-position="top"
    >
      {disabled && disabledReason && <Tooltip target=".form-actions__primary" />}
      <Button
        type={type}
        icon={primaryIcon}
        label={primaryLabel}
        severity={primarySeverity}
        onClick={onPrimary}
        loading={loading}
        disabled={disabled}
      />
    </span>
    {onCancel && (
      <Button
        type="button"
        icon="pi pi-times"
        label="Cancelar"
        outlined
        severity="secondary"
        onClick={onCancel}
      />
    )}
  </div>
);
